import { EnumerationItem } from "./lookup.model";

/**
 * Canonical visit types for scheduling UIs (New Schedule, encounter schedule, Engage schedule).
 * Order is preserved in dropdowns.
 */
export const SCHEDULE_VISIT_TYPE_OPTIONS: ReadonlyArray<{
  value: string;
  displayName: string;
}> = [
  { value: "clinic", displayName: "Clinic" },
  { value: "telehealth", displayName: "Telehealth" },
  { value: "procedure", displayName: "Procedure" },
  { value: "other", displayName: "Other" },
];

/** Legacy visit types still returned by older encounters — used for display/slug resolution only. */
const LEGACY_SCHEDULE_VISIT_TYPE_OPTIONS: ReadonlyArray<{
  value: string;
  displayName: string;
}> = [
  { value: "home", displayName: "Home" },
  { value: "urgent-care", displayName: "Urgent Care" },
  { value: "wellness", displayName: "Wellness" },
];

const ALL_SCHEDULE_VISIT_TYPE_OPTIONS = [
  ...SCHEDULE_VISIT_TYPE_OPTIONS,
  ...LEGACY_SCHEDULE_VISIT_TYPE_OPTIONS,
] as const;

/** Slug → display label (for payloads that expect "Clinic", "Procedure", …). */
export const SCHEDULE_VISIT_TYPE_LABEL_BY_SLUG: Record<string, string> =
  Object.fromEntries(
    SCHEDULE_VISIT_TYPE_OPTIONS.map((o) => [o.value, o.displayName])
  );

export function getScheduleVisitTypeEnumerationItems(): EnumerationItem[] {
  return SCHEDULE_VISIT_TYPE_OPTIONS.map(({ value, displayName }) => ({
    value,
    displayName,
  }));
}

/** Display label for a slug; falls back to the input if unknown. */
export function getScheduleVisitTypeDisplayLabel(slug: string): string {
  const normalized = String(slug ?? "").trim().toLowerCase();
  if (!normalized) {
    return slug;
  }
  const hit = ALL_SCHEDULE_VISIT_TYPE_OPTIONS.find(
    (o) =>
      o.value === normalized ||
      o.displayName.toLowerCase() === normalized
  );
  return hit?.displayName ?? slug;
}

/**
 * Map a stored API value (slug or display label, any case) to canonical slug, or '' if unknown.
 */
export function resolveScheduleVisitTypeSlug(raw: string): string {
  const t = String(raw ?? "").trim();
  if (!t) {
    return "";
  }
  const lower = t.toLowerCase();
  const bySlug = ALL_SCHEDULE_VISIT_TYPE_OPTIONS.find((o) => o.value === lower);
  if (bySlug) {
    return bySlug.value;
  }
  const byLabel = ALL_SCHEDULE_VISIT_TYPE_OPTIONS.find(
    (o) => o.displayName.toLowerCase() === lower
  );
  return byLabel?.value ?? "";
}

/**
 * Human-readable visit type for worksheets, cover sheets, etc.
 * Matches {@link ListViewComponent.getWorksheetVisitTypeDisplay} labels.
 */
export function formatScheduleVisitTypeForDisplay(
  raw: string | null | undefined
): string {
  const value = String(raw ?? "").trim();
  if (!value) {
    return "—";
  }
  const slug = resolveScheduleVisitTypeSlug(value);
  if (slug) {
    return getScheduleVisitTypeDisplayLabel(slug);
  }
  const norm = value.toLowerCase().replace(/[\s_-]+/g, "");
  if (norm === "clinic" || norm === "inperson") {
    return "Clinic";
  }
  if (norm === "telehealth" || norm === "virtual") {
    return "Telehealth";
  }
  if (norm === "home") {
    return "Home";
  }
  if (norm === "urgentcare") {
    return "Urgent Care";
  }
  if (norm === "wellness") {
    return "Wellness";
  }
  return value
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/** True when schedule/appointment visit type resolves to Procedure. */
export function isProcedureScheduleVisit(cache: {
  schedule?: { visitType?: string };
  appointment?: { visitType?: string; encounterType?: string };
} | null | undefined): boolean {
  if (!cache) {
    return false;
  }
  const candidates = [
    cache.schedule?.visitType,
    cache.appointment?.visitType,
    cache.appointment?.encounterType,
  ];
  for (const raw of candidates) {
    if (resolveScheduleVisitTypeSlug(String(raw ?? "")) === "procedure") {
      return true;
    }
  }
  return false;
}

/** Template marker for the auto-created editable notes context on Procedure visits. */
export const PROCEDURE_DEFAULT_NOTES_CONTEXT_TEMPLATE =
  "procedure-default-notes";

/** Default title for the Procedure visit notes context (user may rename in the editor). */
export const PROCEDURE_DEFAULT_NOTES_CONTEXT_TITLE = "Notes";

/**
 * Fallback procedure care-plan template when an encounter has no usable procedure
 * care plan at procedure-room entry (matches manual schedule fix).
 */
export const DEFAULT_PROCEDURE_CARE_PLAN_TEMPLATE_TITLE = "Vascular Procedure";

/** Prompts DB type for AI that splits pasted procedure notes into SOAP sections. */
export const PROCEDURE_NOTES_SOAP_SPLIT_PROMPT_TYPE = "Procedure Notes SOAP Split";

/** Template markers for SOAP contexts created from procedure notes. */
export const PROCEDURE_NOTES_SOAP_SECTION_TEMPLATES = {
  subjective: "procedure-notes-soap-subjective",
  objective: "procedure-notes-soap-objective",
  assessment: "procedure-notes-soap-assessment",
  plan: "procedure-notes-soap-plan",
} as const;

export type ProcedureNotesSoapSectionKey =
  keyof typeof PROCEDURE_NOTES_SOAP_SECTION_TEMPLATES;

export const PROCEDURE_NOTES_SOAP_SECTION_DEFS: ReadonlyArray<{
  key: ProcedureNotesSoapSectionKey;
  contextType: "Subjective" | "Objective" | "Assessment" | "Plan";
  title: string;
}> = [
  { key: "subjective", contextType: "Subjective", title: "Subjective" },
  { key: "objective", contextType: "Objective", title: "Objective" },
  { key: "assessment", contextType: "Assessment", title: "Assessment" },
  { key: "plan", contextType: "Plan", title: "Plan" },
];
